import { PiArrowsClockwise, PiWarningCircle } from 'react-icons/pi';
import Button from 'src/modules/shared/components/atoms/buttons/button';
import { Text } from 'src/modules/shared/components/atoms/text';
import Tooltip from 'src/modules/shared/components/atoms/tooltips/tooltip';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import type { ConversationRealtimeNoticeProps } from '../../types/realtime/conversation-realtime-notice.types';

/**
 * Shows one-row update interruptions with an accessible delayed-state explanation.
 * @param props - Presentation state derived from existing realtime availability.
 * @returns A localized interruption notice, or no element while healthy/disabled.
 */
export const ConversationRealtimeNotice = ({
  state,
}: ConversationRealtimeNoticeProps) => {
  const { t } = useI18n();
  if (!state) return null;
  const Icon = state === 'delayed' ? PiWarningCircle : PiArrowsClockwise;
  const icon = <Icon aria-hidden="true" className="h-4 w-4 shrink-0" />;
  return (
    <div
      role="status"
      aria-live="polite"
      className="flex min-h-[36px] shrink-0 items-center gap-2 border-b border-warning-100 bg-warning-50 px-4 text-warning-800"
    >
      {state === 'delayed' ? (
        <Tooltip
          interactive
          as="span"
          size="sm"
          placement="bottom"
          className="max-w-[min(20rem,calc(100vw-2rem))]"
          contentClassName="shrink-0"
          content={t('communications.realtimeNotice.description')}
        >
          <Button
            appearance="ghost"
            variant="warning"
            size="2xs"
            shape="round"
            isIconOnly
            aria-label={t('communications.realtimeNotice.explanation')}
          >
            {icon}
          </Button>
        </Tooltip>
      ) : (
        <span className="flex h-7 w-7 shrink-0 items-center justify-center">
          {icon}
        </span>
      )}
      <Text size="tiny" weight="medium" className="min-w-0 flex-1 truncate">
        {t(`communications.realtimeNotice.${state}`)}
      </Text>
    </div>
  );
};
